import {
  GenderPreference,
  UserWithMediaAndGroups,
} from "@greenflagdate/shared";
import { useReq } from "@larner.dev/use-req";
import {
  Box,
  Button,
  Flex,
  Text,
  FieldRoot,
  FieldLabel,
  FieldErrorText,
  chakra,
} from "@chakra-ui/react";
import { MouseEventHandler, useCallback, useState } from "react";
import { apiReq } from "../../lib/apiReq";
import { refreshUserState, store } from "../../lib/store/store";
import { ActionType } from "../../lib/store/storeActions";
import { GenderPreferenceSelector } from "../GenderPreferenceSelector/GenderPreferenceSelector";
import { Icon } from "../Icon/Icon";

interface GenderPreferenceScreenProps {
  onClose?: () => void;
  onComplete: () => void;
}

interface GenderPreferenceErrors {
  genderPreference?: string;
}

export const GenderPreferenceScreen = ({
  onClose,
  onComplete,
}: GenderPreferenceScreenProps) => {
  const [genderPreferenceErrors, setGenderPreferenceErrors] =
    useState<GenderPreferenceErrors>({});
  const [genderPreference, setGenderPreference] = useState<
    GenderPreference | undefined
  >();
  const [friendsOnly, setFriendsOnly] = useState(false);
  const [genderPreferenceRequest, genderPreferenceRequestState] =
    useReq<UserWithMediaAndGroups>(apiReq);

  const save = useCallback<MouseEventHandler>(async () => {
    const errors: GenderPreferenceErrors = {};
    // Friends only is a complete answer on its own — it's the one case where
    // a new account never needs a dating preference.
    if (!friendsOnly && !genderPreference) {
      errors.genderPreference =
        "Please tell us who you want to date, or choose friends only.";
    }

    setGenderPreferenceErrors(errors);
    if (!Object.keys(errors).length) {
      genderPreferenceRequestState.clearError();
      const response = await genderPreferenceRequest.put(
        "/user/attributes",
        friendsOnly
          ? { friends_only: true }
          : { gender_preference: genderPreference, friends_only: false }
      );
      if (response.success) {
        store.dispatch({
          type: ActionType.UpdateUser,
          params: { user: response.result },
        });
        // Categories are derived server-side from both friends_only and
        // gender_preference, so either branch above invalidates them.
        await refreshUserState();
        onComplete();
      }
    }
  }, [
    friendsOnly,
    genderPreference,
    genderPreferenceRequestState,
    genderPreferenceRequest,
    onComplete,
  ]);
  return (
    <Box flex={1} px="0.25rem">
      {!!genderPreferenceRequestState.error && (
        <Box
          mb="1rem"
          p="3"
          bg="red.50"
          borderRadius="md"
          borderWidth="1px"
          borderColor="red.500"
        >
          <Text color="red.700" fontWeight="700">
            There was a problem saving
          </Text>
        </Box>
      )}
      <FieldRoot invalid={!!genderPreferenceErrors.genderPreference} mt="1.5rem">
        <FieldLabel>Who do you want to date?</FieldLabel>
        <GenderPreferenceSelector
          value={friendsOnly ? undefined : genderPreference}
          onChange={setGenderPreference}
          disabled={friendsOnly}
        />

        {/* Choosing this instead of a dating preference is a complete answer —
            it mirrors the Friends Only toggle in Dating Preferences, which
            likewise takes the dating options out of play. */}
        <chakra.button
          type="button"
          onClick={() => setFriendsOnly((on) => !on)}
          display="flex"
          alignItems="flex-start"
          gap="0.75rem"
          width="100%"
          mt="1rem"
          p="0.875rem"
          textAlign="left"
          cursor="pointer"
          borderWidth="2px"
          borderStyle="solid"
          borderRadius="0.3125rem"
          borderColor={friendsOnly ? "border.friends" : "border.subtle"}
          bg={friendsOnly ? "bg.friendsSoft" : "bg.canvas"}
          _hover={{ bg: friendsOnly ? "bg.friendsSoft" : "bg.muted" }}
        >
          <Flex
            mt="0.125rem"
            w="1.25rem"
            h="1.25rem"
            flexShrink={0}
            borderRadius="0.25rem"
            borderWidth="2px"
            borderStyle="solid"
            borderColor={friendsOnly ? "border.friends" : "border.default"}
            bg={friendsOnly ? "bg.friends" : "transparent"}
            align="center"
            justify="center"
          >
            {friendsOnly && (
              <Icon name="check" boxSize="0.7rem" color="fg.onBrand" />
            )}
          </Flex>
          <Box>
            <Text fontWeight="600" fontSize="0.9375rem" color="fg.default">
              I'm here for friends, not dating
            </Text>
            <Text fontSize="0.8125rem" color="fg.muted" lineHeight="1.4" mt="0.125rem">
              Your profile won't show up for dating. You'll still match as
              friends after events you attend, and you can change this any time.
            </Text>
          </Box>
        </chakra.button>

        {genderPreferenceErrors.genderPreference && (
          <FieldErrorText>{genderPreferenceErrors.genderPreference}</FieldErrorText>
        )}
      </FieldRoot>

      <Flex
        py="2rem"
        direction={{ base: "column-reverse", sm: "row" }}
        gap="0.65rem"
      >
        {!!onClose && (
          <Button
            variant="ghost"
            flex={1}
            onClick={onClose}
            type="button"
            disabled={genderPreferenceRequestState.loading}
          >
            Cancel
          </Button>
        )}
        <Button
          variant="solid"
          colorPalette="greenDark"
          flex={2}
          disabled={genderPreferenceRequestState.loading}
          onClick={save}
        >
          Save
        </Button>
      </Flex>
    </Box>
  );
};
